/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as adminApi from 'js/utils/adminApi';
import { IAdminUser, IAdminUsersState } from './types';

const initialState: IAdminUsersState = {
    list: [],
    loading: false,
    error: null,
    selected: null,
};

export const fetchAdminUsers = createAsyncThunk<IAdminUser[]>(
    'adminUsers/fetchList',
    async () => adminApi.listUsers()
);

export const createAdminUser = createAsyncThunk<
    IAdminUser,
    adminApi.UserCreatePayload
>('adminUsers/create', async payload => adminApi.createUser(payload));

export const bulkCreateAdminUsers = createAsyncThunk<
    IAdminUser[],
    adminApi.UserCreatePayload[]
>('adminUsers/bulkCreate', async payload => adminApi.bulkCreateUsers(payload));

export const patchAdminUser = createAsyncThunk<
    IAdminUser,
    { id: number; payload: adminApi.UserPatchPayload }
>('adminUsers/patch', async ({ id, payload }) =>
    adminApi.patchUser(id, payload)
);

export const archiveAdminUser = createAsyncThunk<number, number>(
    'adminUsers/archive',
    async id => {
        await adminApi.deleteUser(id);
        return id;
    }
);

export const reissueAdminTicket = createAsyncThunk<void, number>(
    'adminUsers/reissue',
    async id => {
        await adminApi.reissueTicket(id);
    }
);

const adminUsersSlice = createSlice({
    name: 'adminUsers',
    initialState,
    reducers: {
        selectUser(state, action: PayloadAction<IAdminUser | null>) {
            state.selected = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchAdminUsers.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'Unknown error';
            })
            .addCase(createAdminUser.fulfilled, (state, action) => {
                state.list = [action.payload, ...state.list];
            })
            .addCase(bulkCreateAdminUsers.fulfilled, (state, action) => {
                state.list = [...action.payload, ...state.list];
            })
            .addCase(patchAdminUser.fulfilled, (state, action) => {
                state.list = state.list.map(u =>
                    u.id === action.payload.id ? action.payload : u
                );
                if (state.selected?.id === action.payload.id) {
                    state.selected = action.payload;
                }
            })
            .addCase(archiveAdminUser.fulfilled, (state, action) => {
                state.list = state.list.filter(u => u.id !== action.payload);
                if (state.selected?.id === action.payload) {
                    state.selected = null;
                }
            });
    },
});

export const { selectUser } = adminUsersSlice.actions;
export default adminUsersSlice.reducer;
